import React, { useState } from "react";
import AuthShell from "../components/AuthShell";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import GdprConsentBlock from "../components/GdprConsentBlock";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Info } from "lucide-react";

export default function AgencyRegister() {
  const { registerAgency, apiError } = useAuth();
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!acceptedTerms) {
      toast.error("Devi accettare Privacy Policy e Note Legali per registrarti");
      return;
    }
    setLoading(true);
    try {
      await registerAgency({
        full_name: fullName.trim(),
        email: email.trim(),
        password,
        consents: {
          terms_accepted: true,
          marketing_accepted: !!acceptedMarketing,
          policy_version: "2026-05",
        },
      });
      setDone(true);
      toast.success("Richiesta inviata! Attendi l'approvazione dell'amministratore.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      side="agency"
      title="Richiedi accesso — Agenzia"
      subtitle="Compila il form. L'amministratore dovrà approvare la richiesta prima del primo accesso."
      altLinkLabel="Hai già un account? Accedi →"
      altLinkTo="/agenzia/login"
      altLinkDataTestId="agency-register-to-login"
    >
      {done ? (
        <div
          data-testid="agency-register-success"
          className="rounded-2xl border border-[var(--mp-ink)] bg-[var(--mp-yellow)] p-6 shadow-brutal-sm"
        >
          <div className="font-display text-xl">Richiesta inviata ✦</div>
          <p className="mt-2 text-sm">
            L'amministratore riceverà la tua richiesta. Dopo l'approvazione potrai accedere
            dall'area Agenzia con le tue credenziali.
          </p>
        </div>
      ) : (
        <form onSubmit={onSubmit} data-testid="agency-register-form" className="space-y-5">
          <div className="flex items-start gap-3 p-4 rounded-xl border border-[var(--mp-border)] bg-white">
            <Info className="h-4 w-4 mt-0.5 text-[var(--mp-secondary)]" />
            <p className="text-xs text-[var(--mp-muted)] leading-relaxed">
              La registrazione è <strong>solo su approvazione</strong>: dopo l'invio, l'amministratore
              riceverà la tua richiesta.
            </p>
          </div>

          <div>
            <Label htmlFor="full_name" className="text-xs font-bold uppercase tracking-[0.18em]">
              Nome e cognome
            </Label>
            <Input
              id="full_name"
              data-testid="agency-register-fullname"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="mt-2 h-12 rounded-xl border-[var(--mp-ink)]"
              placeholder="Mario Rossi"
            />
          </div>
          <div>
            <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.18em]">
              Email
            </Label>
            <Input
              id="email"
              data-testid="agency-register-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 h-12 rounded-xl border-[var(--mp-ink)]"
              placeholder="nome@agenzia.it"
            />
          </div>
          <div>
            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.18em]">
              Password
            </Label>
            <Input
              id="password"
              data-testid="agency-register-password"
              type="password"
              required
              minLength={8}
              pattern="^(?=.*[A-Za-z])(?=.*\d).{8,}$"
              title="Almeno 8 caratteri, con lettere e numeri"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 h-12 rounded-xl border-[var(--mp-ink)]"
              placeholder="min. 8 caratteri, lettere e numeri"
            />
          </div>
          <GdprConsentBlock
            acceptedTerms={acceptedTerms}
            setAcceptedTerms={setAcceptedTerms}
            acceptedMarketing={acceptedMarketing}
            setAcceptedMarketing={setAcceptedMarketing}
            testidPrefix="agency-register"
          />
          <Button
            type="submit"
            disabled={loading || !acceptedTerms}
            data-testid="agency-register-submit"
            className="w-full h-12 rounded-full bg-[var(--mp-ink)] hover:bg-black text-white"
          >
            {loading ? "Invio richiesta…" : "Invia richiesta"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
